import { apiClient } from "./api-client";
import type {
  Contract,
  ContractViewResponse,
  CreateContractDto,
  AssignContractDto,
  SignContractDto,
  UpdateContractDto,
  EligibleUsersResponse,
  VerifyHashResponse,
} from '@/lib/types/contracts';

const CONTRACT_STATUSES = ['draft', 'unpublished', 'published', 'sent', 'viewed', 'signed', 'final_validated'] as const;
const CONTRACT_TITLES = ['VALIDATION CONTRACT', 'MODERATOR CONTRACT', 'ADMIN CONTRACT'] as const;

const normalizeContractStatus = (status: unknown): Contract['status'] => {
  const value = String(status || '').trim().toLowerCase();
  if ((CONTRACT_STATUSES as readonly string[]).includes(value)) {
    return value as Contract['status'];
  }
  return 'draft' as Contract['status'];
};

const normalizeContractTitle = (title: unknown): Contract['title'] => {
  const value = String(title || '').trim().toUpperCase().replace(/\s+/g, '_');
  if (
    value === 'VALIDATION_CONTRACT' ||
    value === 'MODERATOR_CONTRACT' ||
    value === 'ADMIN_CONTRACT'
  ) {
    return value.replace(/_/g, ' ') as Contract['title'];
  }
  if ((CONTRACT_TITLES as readonly string[]).includes(value.replace(/_/g, ' '))) {
    return value.replace(/_/g, ' ') as Contract['title'];
  }
  return 'VALIDATION CONTRACT' as Contract['title'];
};

const normalizeContract = (raw: Record<string, unknown>): Contract => {
  return {
    ...raw,
    id: String(raw?.id ?? ''),
    status: normalizeContractStatus(raw?.status),
    title: normalizeContractTitle(raw?.title),
  } as Contract;
};

const normalizeContractList = (payload: unknown): Contract[] => {
  const items = Array.isArray(payload) ? payload : Array.isArray((payload as Record<string, unknown>)?.items) ? (payload as Record<string, unknown>).items as unknown[] : [];
  return items.map((item) => normalizeContract(item as Record<string, unknown>));
};

export const contractsApi = {
  /**
   * Create a new contract (Admin only)
   */
  async createContract(dto: CreateContractDto): Promise<Contract> {
    const { data } = await apiClient.post<Contract>(`/contracts`, dto);
    return normalizeContract(data);
  },

  /**
   * Get all contracts for the authenticated user (Admin, Moderator, User)
   */
  async getMyContracts(): Promise<Contract[]> {
    const { data } = await apiClient.get(`/contracts/mine`);
    return normalizeContractList(data);
  },

  /**
   * Get all contracts (Admin only)
   */
  async getContracts(): Promise<Contract[]> {
    const { data } = await apiClient.get(`/contracts`);
    return normalizeContractList(data);
  },

  /**
   * Get active administrator contract template
   */
  async getAdminContractTemplate(): Promise<Contract> {
    const { data } = await apiClient.get('/contracts/templates/admin');
    return normalizeContract(data);
  },

  /**
   * Get contract by id
   */
  async getContractById(contractId: string): Promise<Contract> {
    const { data } = await apiClient.get(`/contracts/${contractId}`);
    return normalizeContract(data);
  },

  /**
   * Get a specific contract (with personalization for assigned user)
   */
  async viewContract(contractId: string): Promise<ContractViewResponse> {
    const { data } = await apiClient.get<ContractViewResponse>(`/contracts/${contractId}/view`);
    return {
      ...data,
      ...normalizeContract(data),
    };
  },

  /**
   * Update a contract (Admin only)
   */
  async updateContract(contractId: string, dto: UpdateContractDto): Promise<Contract> {
    const { data } = await apiClient.patch<Contract>(`/contracts/${contractId}`, dto);
    return normalizeContract(data);
  },

  /**
   * Publish a contract template (Super-admin only)
   */
  async publishContract(contractId: string): Promise<Contract> {
    const { data } = await apiClient.post<Contract>('/contracts/' + contractId + '/publish');
    return normalizeContract(data);
  },

  /**
   * Unpublish a contract template (Super-admin only)
   */
  async unpublishContract(contractId: string): Promise<Contract> {
    const { data } = await apiClient.post<Contract>('/contracts/' + contractId + '/unpublish');
    return normalizeContract(data);
  },

  /**
   * Assign a contract to a specific user (Moderator/Admin only)
   */
  async assignContract(contractId: string, dto: AssignContractDto): Promise<Contract> {
    const { data } = await apiClient.post<Contract>(`/contracts/${contractId}/assign`, dto);
    return normalizeContract(data);
  },

  /**
   * Send the validation contract directly to a user (Moderator/Admin only)
   */
  async sendValidationContract(userId: number): Promise<Contract> {
    const { data } = await apiClient.post<Contract>(`/contracts/send-validation/${userId}`);
    return normalizeContract(data);
  },

  /**
   * Sign a contract (User only)
   */
  async signContract(contractId: string, dto: SignContractDto): Promise<{
    contractId: number;
    status: string;
    signedAt: string;
  }> {
    const signMethodWire = String(dto?.signMethod || '')
      .trim()
      .toLowerCase();

    const payload = {
      ...dto,
      // Backend/DB enum for signatures_sign_method_enum uses lowercase values.
      signMethod: signMethodWire,
    };

    const { data } = await apiClient.post(`/contracts/${contractId}/sign`, payload);
    return data;
  },

  /**
   * Final validation of a signed contract (Moderator/Admin only)
   */
  async finalValidateContract(contractId: string): Promise<Contract> {
    const { data } = await apiClient.post<Contract>(`/contracts/${contractId}/final-validate`);
    return normalizeContract(data);
  },

  /**
   * Verify contract integrity (hash check)
   */
  async verifyContractHash(contractId: string): Promise<VerifyHashResponse> {
    const { data } = await apiClient.get<VerifyHashResponse>(`/contracts/${contractId}/verify`);
    return data;
  },

  /**
   * Get list of eligible users for contract assignment (Moderator/Admin only)
   */
  async getEligibleUsersForContract(contractId: string): Promise<EligibleUsersResponse> {
    const { data } = await apiClient.get<EligibleUsersResponse>(`/contracts/${contractId}/eligible-users`);
    return data;
  },
};



